import Vue from 'vue';
import { getChildren } from './reactive-cache.js';

// A thin wrapper for a doc, providing a prototype with some useful
// properties. Prototype properties are not copied with Object.assign,
// so creating new doc value with Object.assign({}, item, ...) is not
// affected by the wrapper at all.
export function Item(doc, cache, localDB) {
  Object.assign(this, doc);
  Object.defineProperty(this, 'cache', { value: cache });
  Object.defineProperty(this, 'couch', { value: localDB });
  // Folded property will no longer be used. Remove it from the doc so that
  // it doesn't interfere with the new property. The removal will be persisted
  // with any item update that comes next.
  // TODO: Actually remove it from `this`, `doc` is irrelevant at this point.
  delete doc.folded;
}

Item.prototype.save = function () {
  // This is not as circular as it looks, as .couch is not an enumerable property
  // and won't be included in put request.
  return this.couch.put(this)
}

Item.prototype.getChildren = function () {
  let pClass = this._id.split(":")[0];
  if (pClass === "gcal") pClass = "task";
  if (!this.relation || !pClass || !this.cache[this.relation][pClass]) {
    console.error(`Cannot get children of ${this._id} "${this.name}"`);
    return [];
  }
  return getChildren({
    cache: this.cache,
    relation: this.relation,
    pClass,
    pKey: this._id,
  });
}

Object.defineProperties(Item.prototype, {
  localData: {
    get() {
      return this.cache.local[this._id];
    },
  },
  localDataId: {
    get() {
      return '_local/' + this._id;
    },
  },
  updateLocalData: {
    // In-memory update is immediate, but the persistent storage update
    // is async.
    async value(updatesDict, optDeleteList) {
      const current = this.localData || { _id: this.localDataId };
      const modified = Object.assign({}, current, updatesDict);
      if (optDeleteList && optDeleteList.length) {
        for (const attr of optDeleteList) delete modified[attr];
      }
      Vue.set(this.cache.local, this._id, modified);
      // TODO: Handle 409? For now let's assume that the turnover is fast
      // enough to avoid race conditions.
      const response = await this.couch.put(modified);
      this.localData._rev = response.rev;
      return response;
    },
  },
  // Getter for the state of being folded.
  //
  // This should really be called just `folded`, but it clashes with the name
  // already present on some items and causes error when calling Object.assign
  // (as then it tries to assign to a read only property).
  //
  // TODO: Rename back to `folded` once we make sure the `folded` property
  // is permanently purged from the database or we figure out a better way
  // to handle this kind of obsolete fields.
  localFolded: {
    get() {
      if (!this.localData || this.localData.folded === undefined) {
        return true;
      } else {
        return Boolean(this.localData && this.localData.folded);
      }
    },
  },
});

// We don't want to mutate objects from reactive cache - operate on clones.
Item.prototype.clone = function (update = {}) {
  // TODO make this deep copy?
  const clone = new Item(this, this.cache, this.couch)
  if (update.delKey) {
    delete clone[update.delKey]
    delete update.delKey
  }
  Object.assign(clone, update);
  return clone;
}

export function generateId(prefix) {
  const timeString = Date.now().toString(36);
  const randomString = Math.random().toString(36).substring(2);
  return `${prefix}:${timeString}.${randomString}`;
}

export function createList(name, relation, cache, db) {
  const doc = {
    _id: generateId("list"),
    name,
    type: 'list',
    relation,
  };
  new Item(doc, cache, db).save();
  return doc._id;
}

export function createLayout(name, cache, db, layout = []) {
  const doc = {
    _id: generateId("layout"),
    name,
    type: 'layout',
    layout,
  };
  new Item(doc, cache, db).save();
  return doc._id;
}

export function createLabel(name, cache, db) {
  const doc = {
    _id: generateId("label"),
    name,
    type: 'label',
  };
  new Item(doc, cache, db).save();
  return doc._id;
}
